import React, { useState, useEffect, useRef } from 'react';
import { useItems, useStockTransactions } from '../context/DataContext';
import './AddStock.css';

function AddStock() {
  const { data: items, add: addItem, loading: itemsLoading } = useItems();
  const { add: addStockTransaction, loading: txLoading } = useStockTransactions();
  const [mode, setMode] = useState('new'); // 'new' or 'existing'
  const [formData, setFormData] = useState({
    itemCode: '',
    itemName: '',
    size: '',
    weightPerPcs: '',
    totalWeight: '',
    numberOfPackets: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Autocomplete states
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // New state for multiple packet weights
  const [packetWeights, setPacketWeights] = useState([{ id: Date.now(), weight: '' }]);
  const [totalWeight, setTotalWeight] = useState(0);
  const [totalPackets, setTotalPackets] = useState(0);

  // Helper function to round to 2 decimal places
  const roundToTwoDecimals = (value) => {
    return Math.round(value * 100) / 100;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate total weight and packets whenever packet weights change
  useEffect(() => {
    if (mode === 'existing') {
      const weights = packetWeights
        .map(pw => parseFloat(pw.weight))
        .filter(w => !isNaN(w) && w > 0);
      
      const total = weights.reduce((sum, w) => sum + w, 0);
      const roundedTotal = roundToTwoDecimals(total);
      const count = weights.length;
      
      setTotalWeight(roundedTotal);
      setTotalPackets(count);
      
      // Update form data with rounded values
      setFormData(prev => ({
        ...prev,
        totalWeight: roundedTotal.toFixed(2),
        numberOfPackets: count.toString()
      }));
    }
  }, [packetWeights, mode]);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
    setSelectedItem(null);
    setSearchTerm('');
    setFilteredItems([]);
    setShowDropdown(false);
    setPacketWeights([{ id: Date.now(), weight: '' }]);
    setTotalWeight(0);
    setTotalPackets(0);
    setFormData({
      itemCode: '',
      itemName: '',
      size: '',
      weightPerPcs: '',
      totalWeight: '',
      numberOfPackets: '',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-capitalize item code
    let processedValue = value;
    if (name === 'itemCode') {
      processedValue = value.toUpperCase();
    }
    
    setFormData({
      ...formData,
      [name]: processedValue,
    });
    setError('');
    setSuccess('');

    // Check for duplicate item code when in new mode
    if (name === 'itemCode' && mode === 'new' && processedValue) {
      const duplicate = items.find(item => 
        item.itemCode?.toLowerCase() === processedValue.toLowerCase()
      );
      if (duplicate) {
        setError(`❌ Item code "${processedValue}" already exists. Please use a unique code.`);
      }
    }
  };

  // Filter items based on search term
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);
    
    if (value.trim() === '') {
      setFilteredItems([]);
      setSelectedItem(null);
      setPacketWeights([{ id: Date.now(), weight: '' }]);
      setTotalWeight(0);
      setTotalPackets(0);
      setFormData({
        ...formData,
        itemCode: '',
        itemName: '',
        size: '',
        weightPerPcs: '',
        totalWeight: '',
        numberOfPackets: '',
      });
      return;
    }

    const filtered = items.filter(item => {
      const searchLower = value.toLowerCase();
      return (
        (item?.itemCode || '')?.toLowerCase().includes(searchLower) ||
        (item?.itemName || '')?.toLowerCase().includes(searchLower) ||
        (item?.size || '')?.toLowerCase().includes(searchLower)
      );
    });
    setFilteredItems(filtered);
  };

  // Select an item from dropdown
  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setSearchTerm(`${item.itemCode || ''} - ${item.itemName || ''} (${item.size || ''})`);
    setShowDropdown(false);
    setFilteredItems([]);
    
    setPacketWeights([{ id: Date.now(), weight: '' }]);
    setTotalWeight(0);
    setTotalPackets(0);
    
    setFormData({
      ...formData,
      itemCode: item.itemCode || '',
      itemName: item.itemName || '',
      size: item.size || '',
      weightPerPcs: item.weightPerPcs || '0',
      totalWeight: '',
      numberOfPackets: '',
    });
  };

  // Handle packet weight change
  const handlePacketWeightChange = (id, value) => {
    setPacketWeights(prev => 
      prev.map(pw => 
        pw.id === id ? { ...pw, weight: value } : pw
      )
    );
  };

  // Add new packet weight field
  const addPacketField = () => {
    setPacketWeights(prev => [...prev, { id: Date.now(), weight: '' }]);
  };

  // Remove packet weight field
  const removePacketField = (id) => {
    if (packetWeights.length <= 1) {
      setError('❌ At least one packet weight is required');
      return;
    }
    setPacketWeights(prev => prev.filter(pw => pw.id !== id));
  };

  // Highlight matching text in dropdown
  const highlightMatch = (text, search) => {
    const textStr = String(text || '');
    const searchStr = String(search || '');
    
    if (!searchStr.trim() || !textStr) return textStr;
    
    try {
      const parts = textStr.split(new RegExp(`(${searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
      return parts.map((part, i) => 
        part.toLowerCase() === searchStr.toLowerCase() ? 
          <span key={i} className="highlight">{part}</span> : part
      );
    } catch (error) {
      console.error('Error in highlightMatch:', error);
      return textStr;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validation for new item
      if (mode === 'new') {
        if (!formData.itemCode || !formData.itemName || !formData.size || !formData.weightPerPcs) {
          setError('Please fill in all required fields for new item');
          setLoading(false);
          return;
        }

        // Check for duplicate item code before submitting
        const duplicate = items.find(item => 
          (item.itemCode || '').toLowerCase() === formData.itemCode.toLowerCase()
        );
        if (duplicate) {
          setError(`❌ Item code "${formData.itemCode}" already exists. Please use a unique code.`);
          setLoading(false);
          return;
        }

        const data = {
          itemCode: formData.itemCode.trim().toUpperCase(),
          itemName: formData.itemName.trim(),
          size: formData.size.trim(),
          weightPerPcs: roundToTwoDecimals(parseFloat(formData.weightPerPcs) || 0),
          totalWeight: 0,
          numberOfPackets: 0,
        };

        const result = await addItem(data);
        if (result.success) {
          setSuccess('✅ New item added successfully with 0 initial stock!');
          setFormData({
            itemCode: '',
            itemName: '',
            size: '',
            weightPerPcs: '',
            totalWeight: '',
            numberOfPackets: '',
          });
          setSelectedItem(null);
          setSearchTerm('');
          setPacketWeights([{ id: Date.now(), weight: '' }]);
          setTotalWeight(0);
          setTotalPackets(0);
        } else {
          setError('❌ ' + (result.message || 'Failed to add item'));
        }
      } else {
        // Existing mode - add stock
        if (!selectedItem) {
          setError('Please select an existing item from the search');
          setLoading(false);
          return;
        }

        // Validate packet weights
        const validWeights = packetWeights
          .map(pw => parseFloat(pw.weight))
          .filter(w => !isNaN(w) && w > 0);

        if (validWeights.length === 0) {
          setError('Please add at least one valid packet weight (greater than 0)');
          setLoading(false);
          return;
        }

        // Calculate total with rounding
        const totalWeightValue = validWeights.reduce((sum, w) => sum + w, 0);
        const roundedTotalWeight = roundToTwoDecimals(totalWeightValue);

        const result = await addStockTransaction({
          itemCode: formData.itemCode.toUpperCase(),
          itemName: formData.itemName,
          size: formData.size,
          operation: 'IN',
          totalWeight: roundedTotalWeight,
          numberOfPackets: validWeights.length,
        });

        if (result.success) {
          setSuccess(`✅ Stock added successfully! (${validWeights.length} packets, ${roundedTotalWeight.toFixed(2)} g total)`);
          // Reset packet fields but keep item selected
          setPacketWeights([{ id: Date.now(), weight: '' }]);
          setTotalWeight(0);
          setTotalPackets(0);
          setFormData({
            ...formData,
            totalWeight: '',
            numberOfPackets: '',
          });
        } else {
          setError('❌ ' + (result.message || 'Failed to add stock'));
        }
      }
    } catch (err) {
      setError('❌ ' + (err.message || 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const isNewItemMode = mode === 'new';
  const isLoading = loading || itemsLoading || txLoading;

  return (
    <div className="add-stock-page">
      <h1 className="page-title">Add Stock</h1>
      <p className="page-description">
        {isNewItemMode ? 'Add a new item to inventory' : 'Add stock to an existing item'}
      </p>

      <div className="mode-selector">
        <button
          className={`mode-btn ${mode === 'new' ? 'active' : ''}`}
          onClick={() => handleModeChange('new')}
        >
          ➕ New Item
        </button>
        <button
          className={`mode-btn ${mode === 'existing' ? 'active' : ''}`}
          onClick={() => handleModeChange('existing')}
        >
          📥 Add to Existing
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <form onSubmit={handleSubmit} className="stock-form">
        <div className="form-card">
          {mode === 'existing' && (
            <>
              <div className="form-group">
                <label>Search Existing Item *</label>
                <div className="search-container" ref={searchRef}>
                  <input
                    type="text"
                    className="form-control search-input"
                    placeholder="Search by Item Code, Name, or Size..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={() => setShowDropdown(true)}
                    autoComplete="off"
                  />
                  {showDropdown && filteredItems.length > 0 && (
                    <div className="search-dropdown" ref={dropdownRef}>
                      {filteredItems.map(item => (
                        <div
                          key={item.id}
                          className="search-item"
                          onClick={() => handleSelectItem(item)}
                        >
                          <div className="search-item-main">
                            <span className="item-code">
                              {highlightMatch(item.itemCode, searchTerm)}
                            </span>
                            <span className="item-name">
                              {highlightMatch(item.itemName, searchTerm)}
                            </span>
                            <span className="item-size">
                              Size: {highlightMatch(item.size, searchTerm)}
                            </span>
                          </div>
                          <div className="search-item-details">
                            <span className="item-stock">
                              📦 {item.numberOfPackets || 0} packets
                            </span>
                            <span className="item-weight">
                              ⚖️ {item.weightPerPcs || 0} g/pcs
                            </span>
                            <span className="item-total">
                              📊 {item.totalWeight || 0} g
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <small className="help-text">
                  {selectedItem ? '✅ Item selected. Add packet weights below.' : 'Type to search and select an item'}
                </small>
              </div>

              {/* Display selected item details */}
              {selectedItem && (
                <div className="selected-item-details">
                  <div className="detail-row">
                    <span className="detail-label">Code:</span>
                    <span className="detail-value">{selectedItem.itemCode || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{selectedItem.itemName || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Size:</span>
                    <span className="detail-value">{selectedItem.size || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Weight per Piece:</span>
                    <span className="detail-value">{selectedItem.weightPerPcs || 0} g</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Current Stock:</span>
                    <span className="detail-value stock-info">
                      📦 {selectedItem.numberOfPackets || 0} packets | ⚖️ {selectedItem.totalWeight || 0} g
                    </span>
                  </div>
                </div>
              )}

              {/* Packet weights section */}
              {selectedItem && (
                <div className="packet-weights-section">
                  <label className="section-label">Packet Weights (g) *</label>
                  <div className="packet-weights-list">
                    {packetWeights.map((pw, index) => (
                      <div key={pw.id} className="packet-weight-item">
                        <div className="packet-weight-input-group">
                          <input
                            className="form-control packet-weight-input"
                            placeholder={`Packet ${index + 1} weight`}
                            value={pw.weight}
                            onChange={(e) => handlePacketWeightChange(pw.id, e.target.value)}
                            step="0.01"
                            min="0"
                            required
                          />
                          <span className="weight-unit">g</span>
                        </div>
                        <button
                          type="button"
                          className="btn-remove-packet"
                          onClick={() => removePacketField(pw.id)}
                          disabled={packetWeights.length <= 1}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    type="button"
                    className="btn-add-packet"
                    onClick={addPacketField}
                  >
                    ➕ Add Another Packet
                  </button>

                  {/* Summary */}
                  {totalPackets > 0 && (
                    <div className="packet-summary">
                      <div className="summary-item">
                        <span className="summary-label">Total Packets:</span>
                        <span className="summary-value">{totalPackets}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Total Weight:</span>
                        <span className="summary-value">{totalWeight.toFixed(2)} g</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {isNewItemMode && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Item Code *</label>
                  <input
                    type="text"
                    name="itemCode"
                    className="form-control"
                    value={formData.itemCode}
                    onChange={handleChange}
                    placeholder="e.g., ITEM001"
                    required
                  />
                  {formData.itemCode && (
                    <small className={`help-text ${error?.includes('already exists') ? 'error-text' : 'success-text'}`}>
                      {error?.includes('already exists') ? '⚠️ ' : '✓ '}
                      {error?.includes('already exists') ? error : 'Code is available'}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Size *</label>
                  <input
                    type="text"
                    name="size"
                    className="form-control"
                    value={formData.size}
                    onChange={handleChange}
                    placeholder="e.g., 10mm, Medium"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Item Name *</label>
                <input
                  type="text"
                  name="itemName"
                  className="form-control"
                  value={formData.itemName}
                  onChange={handleChange}
                  placeholder="e.g., Steel Rod"
                  required
                />
              </div>

              <div className="form-group">
                <label>Weight per Piece (g) *</label>
                <input
                  name="weightPerPcs"
                  className="form-control"
                  value={formData.weightPerPcs}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                />
                <small className="help-text">Weight of each individual piece</small>
              </div>

              <div className="info-message">
                <p>
                  ℹ️ New items are added with 0 initial stock. You can add stock later using the "Add to Existing" option.
                </p>
              </div>
            </>
          )}

          <button 
            type="submit" 
            className="btn btn-success btn-block" 
            disabled={isLoading || (mode === 'new' && error?.includes('already exists'))}
          >
            {isLoading ? 'Processing...' : isNewItemMode ? 'Add New Item' : 'Add Stock'}
          </button>
        </div>
      </form>

      <div className="quick-info">
        <h3>📋 Quick Tips</h3>
        <ul>
          <li>Item Code must be unique for each item</li>
          <li>Item Code is automatically converted to UPPERCASE</li>
          <li>Use consistent size formats for better filtering</li>
          <li>Weight per piece helps in calculating total weight</li>
          <li>New items start with 0 stock - use "Add to Existing" to add stock later</li>
          <li>Use the search to quickly find existing items</li>
          <li>Add multiple packet weights using the "+" button for bulk stock entry</li>
          <li>All weights are automatically rounded to 2 decimal places</li>
        </ul>
      </div>
    </div>
  );
}

export default AddStock;